import formatDate, { DATE_FORMATS } from 'common/tools/date/format';
import { addClass, removeClass } from 'common/tools/dom/classes';
import readAttribute, {
  readNumberAttribute
} from 'common/tools/dom/readAttribute';
import request from 'common/tools/network/request';
import { path } from 'common/tools/network/routing';
import {
  desobfuscateUrl,
  runAfterDesobfuscation
} from 'common/tools/seo/desobfuscation';
import trans from 'common/tools/translations/trans';
import { sendPageView } from 'common/tracking/jan';

import { BRAND_IS_AC_LIGHT } from 'website/configuration/constants';
import AbstractGallery, {
  cleanDOM,
  comaSeparator,
  fragEl,
  linkEl,
  pipe,
  spanEl
} from 'website/module/ui/async-gallery/AbstractGallery';
import {
  FieldsInfosGql,
  GalleryGqlItem,
  isMovieEntity,
  isPersonEntity,
  MovieEntity
} from 'website/module/ui/async-gallery/types';
import { gemiusHit } from 'website/tracking/gemius';

const fieldsInfos = (() => {
  let infos: FieldsInfosGql[] = [
    {
      transKey: 'moviepage_shot_detail.addedAt',
      field: 'publicationDate',
      className: 'item-inline',
      prefix: null
    },
    {
      transKey: 'moviepage_shot_detail.copyright',
      field: 'copyright',
      className: 'item-inline',
      prefix: pipe.cloneNode(true)
    },
    {
      transKey: 'moviepage_shot_detail.stars',
      field: 'person',
      className: 'item-block',
      prefix: null
    },
    {
      transKey: 'moviepage_shot_detail.description',
      field: 'comments',
      className: 'item-block',
      prefix: null
    },
    {
      transKey: 'moviepage_shot_detail.movie',
      field: 'movie',
      className: 'item-block',
      prefix: null
    },
    {
      transKey: 'moviepage_shot_detail.series',
      field: 'series',
      className: 'item-block',
      prefix: null
    }
  ];

  if (BRAND_IS_AC_LIGHT) {
    infos = [
      {
        transKey: 'moviepage_shot_detail.addedAt',
        field: 'publicationDate',
        className: 'item-inline',
        prefix: null
      },
      {
        transKey: 'moviepage_shot_detail.copyright',
        field: 'copyright',
        className: 'item-inline',
        prefix: pipe.cloneNode(true)
      }
    ];
  }

  return infos;
})();

class AsyncGalleryGql extends AbstractGallery {
  private readonly entityTitle?: string;
  private readonly itemIds?: string | Array<number>;
  private readonly totalLength: number;
  private currentItem: undefined | string | GalleryGqlItem;
  private photoDescription: string | undefined;
  private loading: boolean;
  constructor(element: HTMLElement) {
    super(element);

    this.entityTitle = readAttribute<string, undefined>(
      element,
      'data-entity-title'
    );
    this.itemIds = readAttribute<Array<number>, undefined>(
      element,
      'data-photos-ids'
    );
    this.currentIndex = readNumberAttribute(element, 'data-current-index');
    this.totalLength = readNumberAttribute(element, 'data-total');
    this.currentItem = readAttribute<GalleryGqlItem, undefined>(
      element,
      'data-photo'
    );
    this.photoDescription = undefined;
    this.loading = false;

    this.components = {
      container: this.root.getElementsByClassName('gallery-media-holder')[0],
      counter: this.root.getElementsByClassName('gallery-index')[0],
      figcaption: this.root.getElementsByClassName('gallery-description')[0],
      figure: this.root.getElementsByClassName('gallery-figure')[0],
      image: this.root.getElementsByClassName('gallery-media')[0],
      imageInfos: this.root.getElementsByClassName('gallery-info')[0],
      nextLink: null,
      previousLink: null,
      social: this.root.getElementsByClassName('gallery-social')[0]
    };

    runAfterDesobfuscation(this.finishInitialisation.bind(this));
  }

  changeIndex = async (direction: number) => {
    if (this.loading) {
      return false;
    }

    if (this.currentIndex === 0 && direction === -1) {
      return false;
    }
    if (this.currentIndex === this.totalLength - 1 && direction === +1) {
      return false;
    }

    this.currentIndex = (this.currentIndex ?? 0) + direction;

    const currentItemId = this.itemIds?.[this.currentIndex ?? 0];

    // Await for a xhr call to get the right img
    // if there is any issue on the request we catch the error
    // and change the window.location.href to show the good page
    // if everything goes well we can trigger the render, rewrite history and the ads, ga etc ...
    const locationHref = currentItemId
      ? `${this.startPath}?cmediafile=${currentItemId}`
      : null;

    try {
      this.loading = true;
      if (this.components?.figure) addClass(this.components.figure, 'loading');

      this.currentItem = await request(
        path('internal_api_image_infos', {
          idMedia: currentItemId
        })
      );

      this.loading = false;
      if (this.components?.figure)
        removeClass(this.components.figure, 'loading');
    } catch (e) {
      if (locationHref) window.location.href = locationHref;
      return;
    }

    await this.render();
    this.rewriteHistory(locationHref);
    this.emitUpdate();
    this.changeSocialButtons();
    this.gaPush();
    gemiusHit();
  };

  rewriteHistory = (locationHref?: string | URL | null) => {
    const currentItemId = this.itemIds?.[this.currentIndex ?? 0];
    if (!currentItemId) return;
    window.history.pushState(
      currentItemId,
      this.photoDescription ?? '',
      locationHref
    );
  };

  gaPush = () => {
    const url = `${this.startPath}?cmediafile=${
      this.itemIds?.[this.currentIndex ?? 0]
    }`;
    sendPageView({ url });
  };

  render = async () => {
    if (typeof this.currentItem === 'object') {
      const relatedEntities = this.currentItem?.image?.relatedEntities;
      let movie: MovieEntity | null = null;
      const personsLinks: Node[] = [];
      const personsDescription: string[] = [];
      let photoDescription = this.currentItem.image?.description;

      // handle the type of related entities for photo information
      relatedEntities?.forEach(entity => {
        if (isMovieEntity(entity)) {
          movie = entity;
        }

        if (isPersonEntity(entity)) {
          const personName = `${entity.firstName ?? ''} ${
            entity.lastName ?? ''
          }`;

          const personNameEl = document.createTextNode(personName);

          const personDomLink = entity.seo?.browsable
            ? (() => {
                const link = linkEl.cloneNode() as HTMLAnchorElement;
                link.href = desobfuscateUrl(
                  path('person', {
                    person: entity.internalId
                  })
                );
                link.appendChild(personNameEl);
                return link;
              })()
            : (() => {
                const span = spanEl.cloneNode();
                span.appendChild(personNameEl);
                return span;
              })();
          personsLinks.push(personDomLink);
          personsDescription.push(personName);
        }
      });

      if (!photoDescription) {
        if (personsDescription.length) {
          photoDescription = `${this.entityTitle}: ${personsDescription.join(
            ', '
          )}`;
        } else {
          photoDescription = this.entityTitle;
        }
      }

      this.photoDescription = photoDescription;
      this.setSrcSizeUrl(this.currentItem.image?.path);
      this.changePicture(photoDescription);

      if (this.components?.counter)
        this.components.counter.textContent = `${(this.currentIndex ?? 0) + 1}`; // currentIndex is 0-based

      // prev / next buttons
      if (this.currentIndex === 0) {
        if (this.components?.previousLink)
          addClass(this.components.previousLink, 'hidden');
      } else {
        const previousItemId = this.itemIds?.[(this.currentIndex ?? 0) - 1];
        if (this.components?.previousLink && previousItemId) {
          removeClass(this.components.previousLink, 'hidden');
          this.components.previousLink.href = `${this.startPath}?cmediafile=${previousItemId}`;
        }
      }
      if (this.currentIndex === this.totalLength - 1) {
        if (this.components?.nextLink)
          addClass(this.components.nextLink, 'hidden');
      } else {
        const nextItemId = this.itemIds?.[(this.currentIndex ?? 0) + 1];
        if (this.components?.nextLink && nextItemId) {
          removeClass(this.components.nextLink, 'hidden');
          this.components.nextLink.href = `${this.startPath}?cmediafile=${nextItemId}`;
        }
      }

      // image description
      if (this.components?.figcaption)
        cleanDOM(this.components.figcaption).appendChild(
          document.createTextNode(photoDescription ?? '')
        );
      // document title
      document.title = this.components?.figcaption?.textContent ?? '';
      // image infos

      const infosContent = fieldsInfos.reduce(
        (container: Node, { transKey, field, prefix, className }) => {
          if (typeof this.currentItem === 'object') {
            let value = null;
            if (field === 'publicationDate') {
              value = document.createTextNode(
                formatDate(
                  new Date(
                    this.currentItem.image?.publicationDate?.substring(0, 10) ??
                      ''
                  ),
                  DATE_FORMATS.LONG
                )
              );
            }
            if (
              field === 'copyright' &&
              this.currentItem.image?.data?.[field]
            ) {
              // Replace | for a better readability of actor name (it's returned by cms)
              value = document.createTextNode(
                this.currentItem.image.data[field] ?? ''
              );
            }
            if (field === 'person' && personsLinks.length) {
              // Replace | for a better readability of actor name (it's returned by cms)
              const last = personsLinks.length - 1;
              value = personsLinks.reduce((container, personLink, index) => {
                container.appendChild(personLink);
                if (index < last) {
                  container.appendChild(comaSeparator.cloneNode());
                }
                return container;
              }, fragEl.cloneNode());
            }
            if (field === 'movie' && movie) {
              value = linkEl.cloneNode() as HTMLAnchorElement;
              value.href = desobfuscateUrl(
                path('moviepage', {
                  movie: movie.internalId
                })
              );

              value.appendChild(document.createTextNode(movie.title ?? ''));
            }

            if (value) {
              const spanEl = document.createElement('span');
              spanEl.classList.add(className);

              if (prefix) spanEl.appendChild(prefix);

              spanEl.appendChild(
                document.createTextNode(`${trans(transKey)} `)
              );
              spanEl.appendChild(value);

              container?.appendChild(spanEl);
            }
          }
          return container;
        },
        fragEl.cloneNode()
      );

      if (this.components?.imageInfos)
        cleanDOM(this.components.imageInfos).appendChild(infosContent);
    }
  };
}

export const initGallery = (galleryElement: HTMLElement) => {
  new AsyncGalleryGql(galleryElement);
};
